import StandardLayout from "components/StandardLayout";
import { useParams } from "react-router-dom";
import LeftMenu from "./leftMenu/LeftMenu.jsx";
import ProductPickerModal from "./ProductPickerModal.jsx";
import ProjectDetailsModal from "components/ProjectDetailsModal";
import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getFirestore, onSnapshot, runTransaction } from "firebase/firestore";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDatabaseReader } from "database/useDatabaseReader";
import ColorCard from "components/ColorCard";
import { useCopyToClipboard } from "helpers/useCopyToClipboard";
import { onUpdateProjectSearchableData } from "database/projectsWriter";
import { ClipboardPaste, CopyPlus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useProjectBuilderInventory } from "./useProjectBuilderInventory";
import ConfirmModal from "components/ConfirmModal";
import { onDeleteProject } from "database/projectsWriter";
import useNavigation from "helpers/useNavigation";
import { useAuth } from "database/useAuth";
import { getProjectAuthority, NOT_MOVED } from "services/projectAuthority";
import { getV2ProjectUrl } from "services/v2Portal";

export const ProjectBuilderPage = () => {
  const { projID } = useParams();
  const [, copy] = useCopyToClipboard();
  const [navigate] = useNavigation();
  const { user } = useAuth();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { warehouses } = useDatabaseReader();
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [project, setProject] = useState();

  // Data authority for this project. movedToV2 renders the builder read-only
  // (banner + write sites disabled); the check fails open, and the V2-side
  // backstop trigger is the enforcement wall regardless.
  const [authority, setAuthority] = useState(NOT_MOVED);
  const isReadOnly = authority.movedToV2;

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;
    getProjectAuthority(user.uid, projID).then((result) => {
      if (!cancelled) setAuthority(result);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, projID]);

  const { addArticleForInventory, inventory, inventoryLoading, isInventoryStale, minutesSinceInventoryUpdate, articles } =
    useProjectBuilderInventory(projID, warehouses);

  const sharableLink = useMemo(() => {
    let domain = "";
    if (
      window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("192.168") ||
      window.location.hostname.includes("bs-local") ||
      window.location.hostname.includes("scotts-macbook-pro-2023.local")
    ) {
      domain = `http://${window.location.hostname}:${window.location.port}`;
    } else {
      domain = `https://${window.location.hostname}`;
    }

    return domain.concat(`/public/sharing/${projID}`);
  }, [projID]);

  // Flatten the sorted + selected-filtered cards into one list, then virtualize
  // it so only the on-screen rows mount. A large project (300+ items) otherwise
  // renders hundreds of heavy ColorCards at once and freezes the builder.
  const visibleCards = useMemo(() => {
    const selectedIds = new Set(
      project?.selectedItems?.map((item) => item.id) ?? [],
    );
    // Dedupe by colour id. Incremental appends can leave more than one cached
    // article group for the same item_number (e.g. a fast double-add), and we
    // no longer rebuild the cache on every snapshot, so guard against rendering
    // the same id twice (which would be a duplicate React key).
    const seen = new Set();
    const result = [];
    [...(articles ?? [])]
      .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber))
      .forEach((article) => {
        article.colors.forEach((color) => {
          if (selectedIds.has(color.id) && !seen.has(color.id)) {
            seen.add(color.id);
            result.push(color);
          }
        });
      });
    return result;
  }, [articles, project?.selectedItems]);

  const scrollRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: visibleCards.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 420,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 6,
  });

  useEffect(() => {
    const db = getFirestore();

    const unsub = onSnapshot(doc(db, "projects", projID), (doc) => {
      if (!doc.exists()) return;
      setProject({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate(),
        modifiedAt: doc.data().modifiedAt?.toDate(),
      });
    });

    return unsub;
  }, [projID]);



  const notifyReadOnly = () => {
    toast.info(
      "This project has moved to the new platform and is read-only here",
    );
  };

  const onSelectItem = async (id) => {
    if (project == null) return;
    if (isReadOnly) {
      notifyReadOnly();
      return;
    }

    // Optimistic display append for adds (best-effort; the snapshot reload is
    // authoritative). addArticleForInventory no-ops if the item is already shown.
    const alreadySelected = project.selectedItems?.some(
      (selection) => selection.id === id,
    );
    if (!alreadySelected) addArticleForInventory(id);

    // Toggle selectedItems inside a transaction so the read-modify-write is
    // based on the LATEST server state, not stale React state. Without this,
    // rapid successive adds on a large project each read the same stale array
    // and overwrite each other, so newly-added styles silently vanish (the +
    // flips back orange→black). The transaction retries on contention, which
    // serializes fast clicks instead of clobbering them.
    try {
      const db = getFirestore();
      const ref = doc(db, "projects", projID);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const current = snap.data()?.selectedItems ?? [];
        const exists = current.some((selection) => selection.id === id);
        const next = exists
          ? current.filter((selection) => selection.id !== id)
          : [...current, { id: id, dateAdded: new Date() }];
        tx.update(ref, { selectedItems: next });
      });
    } catch (error) {
      console.log("Error toggling item in project", error);
    }
  };

  const onSaveDetails = async (
    projectName,
    distName,
    acctNumber,
    distRep,
    collaboratorIDs,
  ) => {
    if (isReadOnly) {
      notifyReadOnly();
      return;
    }
    console.log(projectName, distName, acctNumber, distRep, collaboratorIDs);
    onUpdateProjectSearchableData(
      projID,
      projectName,
      distName,
      acctNumber,
      distRep,
      collaboratorIDs,
    );
  };

  return (
    <StandardLayout
      isMenuOpen={isMenuOpen}
      setIsMenuOpen={setIsMenuOpen}
      leftMenuContent={
        <LeftMenu
          project={project}
          onSelectEditDetails={() => setShowEditDetails(true)}
          readOnly={isReadOnly}
        />
      }
    >
      <div className="grid h-full w-full grid-rows-[auto_1fr] overflow-hidden">
        <div>
          {isReadOnly && (
            <div className="z-10 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-st-orange px-3 py-2 text-center text-xs font-bold uppercase text-white md:text-sm">
              <span>
                This project has moved to the new platform and is read-only
                here
              </span>
              <a
                className="underline hover:text-st-grey"
                href={getV2ProjectUrl(authority.v2Id)}
              >
                Open in the new platform
              </a>
            </div>
          )}
          <div className="z-10 flex w-full items-center justify-around gap-4 bg-white px-3 py-2 text-text shadow-md md:justify-normal">
            <img
              className="md:max-w-24 max-w-16 max-h-8 object-contain md:max-h-8"
              src={project?.logoURL || "/stormtech_icon_placeholder.svg"}
              alt="Company Logo"
            />
            {!isReadOnly && (
              <>
                <div className="h-full border-r border-secondary"></div>
                <button
                  className="flex cursor-pointer items-center px-1 py-1 text-xs font-bold uppercase hover:text-st-orange md:px-3 md:text-base"
                  onClick={() => setShowProductPicker(true)}
                >
                  <CopyPlus className="mr-2 h-5 w-5" /> Add Items
                </button>
              </>
            )}
            <div className="h-full border-r border-secondary"></div>
            <button
              className="flex cursor-pointer items-center px-1 py-1 text-xs font-bold uppercase hover:text-st-orange md:px-3 md:text-base"
              onClick={() => {
                copy(sharableLink);
                toast.success("Copied to clipboard");
              }}
            >
              <ClipboardPaste className="mr-2 h-5 w-5" /> Copy Link
            </button>
            {!isReadOnly && (
              <div className="flex h-full grow items-center justify-end gap-4">
                <div className="h-full border-r border-secondary"></div>
                <button
                  className="cursor-pointer hover:text-red-700"
                  onClick={() => setShowConfirmDelete(true)}
                >
                  <Trash2 />
                </button>
              </div>
            )}
          </div>
        </div>
        <div ref={scrollRef} className="h-full overflow-auto bg-secondary p-3">
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: "relative",
              width: "100%",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = visibleCards[virtualRow.index];
              return (
                <div
                  key={item.id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  className="pb-3"
                >
                  <ColorCard
                    productData={item}
                    selectedWarehouses={project?.warehouses}
                    currency={project?.currency}
                    showItemNumber={true}
                    liveInventory={inventory}
                    isLoadingInventory={inventoryLoading}
                    isSelectable={!isReadOnly}
                    isSelected={true}
                    onSelect={onSelectItem}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ProductPickerModal
        isOpen={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        selectedItems={project?.selectedItems?.map((item) => item.id)}
        onSelectItem={onSelectItem}
        project={project}
      />
      <ProjectDetailsModal
        isOpen={showEditDetails}
        onClose={() => setShowEditDetails(false)}
        title="Edit Details"
        buttonTitle="Save"
        initialValues={{
          name: project?.name,
          distributor: project?.distributor,
          accountNumber: project?.accountNumber,
          rep: project?.rep,
          collaborators: project?.users,
        }}
        onSave={onSaveDetails}
      />
      <ConfirmModal
        title="Confirm Delete"
        message={
          <div>
            <p>Are you sure you want to delete the project:</p>
            <p>{project?.name}</p>
          </div>
        }
        buttonTitle="Delete"
        show={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        isDestructive
        delayConfirm
        onConfirm={() => {
          if (isReadOnly) {
            notifyReadOnly();
            return;
          }
          onDeleteProject(projID);
          navigate("/private/projects", `d=${projID}`);
        }}
      />
    </StandardLayout>
  );
};
